import { useMemo } from 'react';
import {
  useAreesAdmin,
  useDepartamentsAdmin,
  useServeisAdmin,
  type ServeiAdminDto,
} from '@/api/administracio';
import type { AreaDto, DepartamentDto } from '@/api/planificacio';
import type { OpcioDesplegable } from '@/components/Desplegable';

export type DepartamentEstructura = DepartamentDto & {
  actiu?: boolean;
  version?: number;
  arees: AreaEstructura[];
};
export type AreaEstructura = AreaDto & {
  descripcio?: string | null;
  actiu?: boolean;
  version?: number;
  serveis: ServeiAdminDto[];
};

export type TipusNode = 'DEPARTAMENT' | 'AREA' | 'SERVEI';

export interface Estructura {
  departaments: DepartamentEstructura[];
  carregant: boolean;
  error: boolean;
  refetch: () => void;
  departament: (id?: number | null) => DepartamentEstructura | undefined;
  area: (id?: number | null) => AreaEstructura | undefined;
  servei: (id?: number | null) => ServeiAdminDto | undefined;
  /** «Tecnologia › Sistemes IT» per a un servei o àrea. */
  ruta: (tipus: TipusNode, id?: number | null) => string;
}

const actiu = (x: { actiu?: boolean }) => x.actiu !== false;

/**
 * Departaments › Àrees › Serveis en un sol arbre (ordre alfabètic), muntat a partir de les
 * tres llistes d'administració. Base de l'arbre d'Estructura i dels selectors de servei/àmbit.
 */
export function useEstructura(): Estructura {
  const deps = useDepartamentsAdmin();
  const arees = useAreesAdmin();
  const serveis = useServeisAdmin();
  return useMemo(() => {
    const perNom = <T extends { nom: string }>(a: T, b: T) => a.nom.localeCompare(b.nom);
    const llistaServeis = [...(serveis.data ?? [])].sort(perNom);
    const llistaArees: AreaEstructura[] = [...(arees.data ?? [])].sort(perNom).map((a) => ({
      ...a,
      serveis: llistaServeis.filter((s) => s.areaId === a.id),
    }));
    const departaments: DepartamentEstructura[] = [
      ...((deps.data ?? []) as (DepartamentDto & { actiu?: boolean; version?: number })[]),
    ]
      .sort(perNom)
      .map((d) => ({ ...d, arees: llistaArees.filter((a) => a.departamentId === d.id) }));
    const departament = (id?: number | null) => departaments.find((d) => d.id === id);
    const area = (id?: number | null) => llistaArees.find((a) => a.id === id);
    const servei = (id?: number | null) => llistaServeis.find((s) => s.id === id);
    const ruta = (tipus: TipusNode, id?: number | null) => {
      if (tipus === 'DEPARTAMENT') return departament(id)?.nom ?? '';
      const a = tipus === 'AREA' ? area(id) : area(servei(id)?.areaId);
      const d = departament(a?.departamentId);
      return [d?.nom, tipus === 'SERVEI' ? a?.nom : null].filter(Boolean).join(' › ');
    };
    return {
      departaments,
      carregant: deps.isLoading || arees.isLoading || serveis.isLoading,
      error: deps.isError || arees.isError || serveis.isError,
      refetch: () => {
        void deps.refetch();
        void arees.refetch();
        void serveis.refetch();
      },
      departament,
      area,
      servei,
      ruta,
    };
    // Les funcions refetch són estables; només depenem de les dades.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deps.data,
    arees.data,
    serveis.data,
    deps.isLoading,
    arees.isLoading,
    serveis.isLoading,
    deps.isError,
    arees.isError,
    serveis.isError,
  ]);
}

/**
 * Opcions de servei per a un `Desplegable`, agrupades per «Departament › Àrea».
 * Per defecte només serveis actius (més el seleccionat, perquè no desaparegui).
 */
export function opcionsServei(
  e: Estructura,
  { inactius = false, seleccionat }: { inactius?: boolean; seleccionat?: number | null } = {},
): OpcioDesplegable<number>[] {
  const out: OpcioDesplegable<number>[] = [];
  for (const d of e.departaments)
    for (const a of d.arees)
      for (const s of a.serveis) {
        if (!inactius && !actiu(s) && s.id !== seleccionat) continue;
        out.push({
          valor: s.id,
          etiqueta: s.nom,
          detall: s.codi ?? undefined,
          grup: `${d.nom} › ${a.nom}`,
        });
      }
  return out;
}

/** Opcions d'àrea agrupades per departament. */
export function opcionsArea(
  e: Estructura,
  { inactius = false, seleccionat }: { inactius?: boolean; seleccionat?: number | null } = {},
): OpcioDesplegable<number>[] {
  return e.departaments.flatMap((d) =>
    d.arees
      .filter((a) => inactius || actiu(a) || a.id === seleccionat)
      .map((a) => ({ valor: a.id, etiqueta: a.nom, detall: a.codi ?? undefined, grup: d.nom })),
  );
}

/** Opcions de departament. */
export function opcionsDepartament(
  e: Estructura,
  { inactius = false, seleccionat }: { inactius?: boolean; seleccionat?: number | null } = {},
): OpcioDesplegable<number>[] {
  return e.departaments
    .filter((d) => inactius || actiu(d) || d.id === seleccionat)
    .map((d) => ({ valor: d.id, etiqueta: d.nom, detall: d.codi ?? undefined }));
}

export interface NodePla {
  tipus: TipusNode;
  id: number;
  nom: string;
  codi?: string | null;
  /** On és dins la jerarquia (buit per als departaments). */
  ruta: string;
  actiu: boolean;
}

/** Tots els nodes en una llista plana (per cercar àmbits). */
export function nodesPlans(e: Estructura): NodePla[] {
  const out: NodePla[] = [];
  for (const d of e.departaments) {
    out.push({
      tipus: 'DEPARTAMENT',
      id: d.id,
      nom: d.nom,
      codi: d.codi,
      ruta: '',
      actiu: actiu(d),
    });
    for (const a of d.arees) {
      out.push({ tipus: 'AREA', id: a.id, nom: a.nom, codi: a.codi, ruta: d.nom, actiu: actiu(a) });
      for (const s of a.serveis)
        out.push({
          tipus: 'SERVEI',
          id: s.id,
          nom: s.nom,
          codi: s.codi,
          ruta: `${d.nom} › ${a.nom}`,
          actiu: actiu(s),
        });
    }
  }
  return out;
}

export const esActiu = actiu;
